import { TabKeys } from '@/constants/tab'
import { ThemeKeys } from '@/constants/global'
import type { Project } from '@/typings/project'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ImageClarity } from '@/constants/image'
import { BonjourStatus } from '@/typings/base'
import type { Lang } from '@/typings/i18n'
import { LoginType } from '@/constants/account'
import type { SellInfo } from '@/typings/subscribe'

export const useGlobalStore = defineStore(
  'global',
  () => {
    /**
     * 当前主题
     */
    const currentTheme = ref<ThemeKeys>(ThemeKeys.DARK)
    /**
     * 当前功能卡片名称，创意或微调
     */
    const currentTabName = ref<TabKeys | string>(TabKeys.ORIGINALITY)
    /**
     * 当前路由页面名称
     */
    const currentViewName = ref<string>('')
    /**
     * 当前项目信息
     */
    const currentProject = ref<Project | null>(null)
    /**
     * 是否第一次去背景操作
     */
    const fistRemoveBg = ref<boolean>(true)
    /**
     * 去背景参考时间
     */
    const removeBgTime = ref<number>(0)
    /**
     * 是否移动端浏览器
     */
    const isMobile = ref<boolean>(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
        navigator.userAgent
      ) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    )
    /**
     * 是否已触发更新
     */
    const hasUpdate = ref<boolean>(false)
    /**
     * 是否有更新
     */
    const updateAvailable = ref<boolean>(false)
    /**
     * 是否正在检查更新
     */
    const isCheckingUpdate = ref<boolean>(false)
    /**
     * 是否已查询过跳转邀请码页面接口，防止每次路由跳转都要请求
     */
    const isRequireInvitationCode = ref<boolean>(false)
    /**
     * 是否需要跳转邀请码页面
     */
    const isToInvitationCode = ref<boolean>(false)
    /**
     * 操作系统信息
     */
    const envInfo = ref<Record<string, string>>({})
    /**
     * 是否需要安装bonjour服务
     */
    const requireBonjour = ref<BonjourStatus>(BonjourStatus.OK)
    /**
     * 语言
     */
    const language = ref<Lang | null>(null)
    /**
     * 网络是否可用
     */
    const networkAvailable = ref<boolean>(true)
    /**
     * 服务器访问是否可用
     */
    const serverAvailable = ref<boolean>(true)
    /**
     * 近期登录
     */
    const recentLogin = ref<LoginType | null>(null)
    /**
     * 月付会员列表
     */
    const monthlySellList = ref<SellInfo[]>([])
    /**
     * 年付会员列表
     */
    const yearlySellList = ref<SellInfo[]>([])

    const imageRect = computed(() => {
      // if (currentProject.value?.sizeType === SizeType['1:1']) {
      //   return {
      //     imageWidth: 1920,
      //     imageHeight: 1920
      //   }
      // }
      return {
        imageWidth: 1280,
        imageHeight: 1920
      }
    })

    const displayProjectName = ref<string>('')

    const imageSaveType = ref<ImageClarity>(ImageClarity.SD)
    const chromeExist = ref<boolean>(false)
    const consentState = ref<string>('')
    const appVersion = ref<string>('')
    // 是否开启生成图片音效
    const generateImageSound = ref<boolean>(true)
    const triggerData = ref<{ trigger: string; trid: string } | null>(null)
    // 是否显示预发布公告框
    const showedPreAnnouncementTips = ref<boolean>(false)
    // 是否显示删除插件提示框
    const showDeletePluginTip = ref<boolean>(true)
    // 是否显示插件提示框
    // const showedPluginsTips = ref<boolean>(false)
    // 是否已显示公告框
    const showedAnnouncementTips = ref<boolean>(false)
    // 是否已显示年付优惠券弹框
    const showedYearlyCouponDialog = ref<boolean>(false)
    // 上一次的点数
    const lastPoints = ref<number>(0)
    // 客户端标识，用于事件上报
    const clientId = ref<string>('')

    const setCurrentTheme = (theme: ThemeKeys) => (currentTheme.value = theme)
    const setCurrentTabName = (name: TabKeys | string) => (currentTabName.value = name)
    const setCurrentViewName = (viewName: string) => (currentViewName.value = viewName)
    const setCurrentProject = (project: Project | null) => (currentProject.value = project)
    const setFistRemoveBg = (is: boolean) => (fistRemoveBg.value = is)
    const setRemoveBgTime = (time: number) => (removeBgTime.value = time)
    const setDisplayProjectName = (name: string) => (displayProjectName.value = name)
    const setImageSaveType = (type: ImageClarity) => (imageSaveType.value = type)
    const setHasUpdate = (update: boolean) => (hasUpdate.value = update)
    const setUpdateAvailable = (available: boolean) => (updateAvailable.value = available)
    const setIsCheckingUpdate = (is: boolean) => (isCheckingUpdate.value = is)
    const setIsRequireInvitationCode = (is: boolean) => (isRequireInvitationCode.value = is)
    const setIsToInvitationCode = (is: boolean) => (isToInvitationCode.value = is)
    const setEnvInfo = (info: Record<string, string>) => (envInfo.value = info)
    const setRequireBonjour = (require: BonjourStatus) => (requireBonjour.value = require)
    const setLanguage = (lang: Lang) => (language.value = lang)
    const setNetworkAvailable = (available: boolean) => (networkAvailable.value = available)
    const setServerAvailable = (available: boolean) => (serverAvailable.value = available)
    const setChromeExist = (exist: boolean) => (chromeExist.value = exist)
    const setConsentState = (state: string) => (consentState.value = state)
    const setAppVersion = (version: string) => (appVersion.value = version)
    const setGenerateImageSound = (sound: boolean) => (generateImageSound.value = sound)
    const setRecentLogin = (login: LoginType | null) => (recentLogin.value = login)
    const setTriggerData = (ltr: { trigger: string; trid: string } | null) => (triggerData.value = ltr)
    // const setShowedPluginsTips = (show: boolean) => (showedPluginsTips.value = show)
    const setShowedPreAnnouncementTips = (show: boolean) => (showedPreAnnouncementTips.value = show)
    const setShowDeletePluginTip = (show: boolean) => (showDeletePluginTip.value = show)
    const setShowedAnnouncementTips = (show: boolean) => (showedAnnouncementTips.value = show)
    const setMonthlySellList = (list: SellInfo[]) => (monthlySellList.value = list)
    const setYearlySellList = (list: SellInfo[]) => (yearlySellList.value = list)
    const setShowedYearlyCouponDialog = (show: boolean) => (showedYearlyCouponDialog.value = show)
    const setLastPoints = (points: number) => (lastPoints.value = points)
    const setClientId = (id: string) => (clientId.value = id)

    return {
      currentTheme,
      currentTabName,
      currentViewName,
      currentProject,
      fistRemoveBg,
      removeBgTime,
      isMobile,
      imageRect,
      displayProjectName,
      imageSaveType,
      hasUpdate,
      updateAvailable,
      isCheckingUpdate,
      isRequireInvitationCode,
      isToInvitationCode,
      envInfo,
      requireBonjour,
      language,
      networkAvailable,
      serverAvailable,
      chromeExist,
      consentState,
      appVersion,
      generateImageSound,
      recentLogin,
      triggerData,
      // showedPluginsTips,
      showedPreAnnouncementTips,
      showDeletePluginTip,
      showedAnnouncementTips,
      monthlySellList,
      yearlySellList,
      showedYearlyCouponDialog,
      lastPoints,
      clientId,
      setCurrentTheme,
      setCurrentTabName,
      setCurrentViewName,
      setCurrentProject,
      setFistRemoveBg,
      setRemoveBgTime,
      setDisplayProjectName,
      setImageSaveType,
      setHasUpdate,
      setUpdateAvailable,
      setIsCheckingUpdate,
      setIsRequireInvitationCode,
      setIsToInvitationCode,
      setEnvInfo,
      setRequireBonjour,
      setLanguage,
      setNetworkAvailable,
      setServerAvailable,
      setChromeExist,
      setConsentState,
      setAppVersion,
      setGenerateImageSound,
      setRecentLogin,
      setTriggerData,
      setShowedPreAnnouncementTips,
      // setShowedPluginsTips,
      setShowDeletePluginTip,
      setShowedAnnouncementTips,
      setMonthlySellList,
      setYearlySellList,
      setShowedYearlyCouponDialog,
      setLastPoints,
      setClientId
    }
  },
  {
    persist: {
      paths: ['currentTheme', 'currentProject', 'displayProjectName', 'language', 'consentState', 'generateImageSound', 'recentLogin', 'showedPreAnnouncementTips', 'showDeletePluginTip', 'showedAnnouncementTips', 'showedPluginsTips', 'showedYearlyCouponDialog', 'lastPoints']
    }
  }
)
